<script setup>
import {useStore} from "vue2-helpers/vuex";
import teamService from "@/services/team.service";
import {ref, computed, onMounted, watch, provide} from "vue";
import UserEdit from "@/components/team/UserEdit.vue";
import UsersList from "@/views/team/UsersList.vue";
import EditGroup from "@/components/team/EditGroup.vue";
import {getNoun} from "@/helpers/string";

const store = useStore();

const search = ref('');
const users = ref([]);
const groups = ref([]);
const editUser = ref(null);
const editGroup = ref(null);

const adminUsers = computed(() => users.value.filter(user => user.is_admin));
const commonUsers = computed(() => users.value.filter(user => !user.is_admin && !user.is_explorer));
const explorerUsers = computed(() => users.value.filter(user => user.is_explorer));

watch(() => store.state.needUpdateUsers, (value) => {
    if (value) {
        getLists();
    }
});

onMounted(() => {
    getLists();
})

async function getUsersList() {
    const {data} = await teamService.getUsersIndex(search.value);
    users.value = data;
}

async function getGroupsList() {
    if (store.getters.isEnterprise) {
        const {data} = await teamService.getGroupsIndex(search.value);
        groups.value = data;
    }
}

async function deleteGroup(id) {
    if (!confirm("Remove group?")) return;

    await teamService.deleteGroup(id);
    getGroupsList();
}

function getLists() {
    getUsersList();
    getGroupsList();
}

provide('getUsersList', getUsersList);
</script>

<template>
    <div v-if="
        (store.getters.isEnterprise && (store.getters.permissions.includes('team-manage') || store.state.user.admin_groups.length))
        || (store.getters.isEcosystem && store.getters.isAdmin)
        || (store.getters.isStartup && store.getters.isAdmin)
    ">
        <div class="tracker-section tracker-section--inline pb-4">
            <h2 class="tracker-name heading2 heading2--tertiary">
                Team
            </h2>

            <input
                v-model="search"
                type="text"
                v-debounce:500="getLists"
                class="form-search-round"
                placeholder="Search for a user or group"
            />
        </div>

        <div class="main-inner">
            <template v-if="store.getters.isEnterprise && !store.getters.onlyNeeds">
                <header class="main-inner__header">
                    <h2 class="heading2">Groups</h2>

                    <a class="link" v-if="store.getters.permissions.includes('team-manage')"
                       @click="editGroup.open(0)">+ New Group</a>
                </header>

                <div class="row main-inner__content">
                    <div class="col-md-6 col-lg-4" v-for="group in groups" :key="group.id">

                        <div class="card card--bordered">
                            <header class="card__header">
                                <h4 class="heading4 heading4--secondary">
                                    <router-link :to="'/groups/' + group.id">{{ group.name }}</router-link>
                                </h4>

                                <Dropdown class="dropdown-original dropdown-incard icons-list dropdown-small ml-auto">
                                    <DropdownItem class="icons-list__edit" @click="editGroup.open(group.id)">
                                        Edit Group
                                    </DropdownItem>

                                    <DropdownItem
                                        v-if="store.getters.permissions.includes('team-manage')"
                                        class="icons-list__delete"
                                        @click="deleteGroup(group.id)"
                                    >
                                        Remove Group
                                    </DropdownItem>
                                </Dropdown>
                            </header>

                            <div class="card__collapse">
                                <h5 class="heading5 heading5--secondary mb-0">{{ group.users_count }}
                                    {{ getNoun('user', group.users_count) }}</h5>
                            </div>
                        </div>
                    </div>
                </div>
            </template>

            <header class="main-inner__header">
                <h2 class="heading2">Admins</h2>

                <a
                    v-if="
                        (store.getters.isEnterprise && store.getters.permissions.includes('team-manage') && !store.getters.settings.disable_add_users && !store.getters.onlyNeeds)
                        || store.getters.isEcosystem
                        || store.getters.isStartup
                    "
                    class="link"
                    @click="editUser.open(0, true)"
                >+ Add Admin</a>
            </header>

            <UsersList
                :users="adminUsers"
                type="admin"
            />

            <template v-if="!store.getters.onlyNeeds">
                <header class="main-inner__header">
                    <h2 class="heading2">Users</h2>

                    <a
                        v-if="
                            (store.getters.isEnterprise && !store.getters.settings.disable_add_users)
                            || store.getters.isEcosystem
                            || store.getters.isStartup
                        "
                        class="link"
                        @click="editUser.open()"
                    >+ Add User</a>
                </header>

                <UsersList
                    :users="commonUsers"
                    type="common"
                />
            </template>

            <template v-if="store.getters.isEnterprise">
                <header class="main-inner__header">
                    <h2 class="heading2">Explorers</h2>

                    <a
                        v-if="!store.getters.settings.disable_add_users"
                        class="link"
                        @click="editUser.open(0, false, true)"
                    >+ Add Explorer</a>
                </header>

                <UsersList
                    :users="explorerUsers"
                    type="explorer"
                />
            </template>
        </div>

        <UserEdit
            ref="editUser"
            @saved="getUsersList"
        />

        <EditGroup
            ref="editGroup"
            @groupSaved="getGroupsList"
        />
    </div>
</template>

<style scoped>

</style>
